import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import date from 'date-and-time';
import { ButtonPrimary, Caption, Box } from '@comeon/ui';

import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectLocale, selectBalance, selectCurrency } from '~/components/account/ducks/index.js';
import Icon, { ICON_BOTTOM } from '~/components/icon/index.js';
import Collapse from './collapse.js';
import currencyFormatter from '../../../../libs/currency-formatter.js';
import { showNotification } from '../../../notifications/ducks/notifications.js';
import useTranslations from '../../../translation/use-translations';
import useIsMounted from '~/hooks/use-is-mounted';
import {
	spectatorRegister,
	spectatorRegisterFreeWespin,
	fetchProfile,
	setTemporaryBuyIn,
	setPlayerBalanceInEventCurrency,
	playerBalanceInEventCurrency,
	fetchPlayersBoughtIntoSession,
	fetchHasMadeDeposit
} from '../ducks/we-spin-duck.js';
import {
	selectDisplayName,
	selectTemporaryBuyIn,
	selectPlayerBalanceInEventCurrency,
	selectPlayersBoughtIntoSession,
	selectHasMadeDeposit,
	selectConfiguration,
	selectWeSpinBuyInEnabled
} from '../ducks/we-spin-selectors.js';
import abbrNum from '../utils/abbreviate-number.js';

import {
	WE_SPIN_BUY_IN_AMOUNT_BALANCE_CAP_EXCEEDED,
	WE_SPIN_BUY_IN_LOW_AMOUNT,
	WE_SPIN_BUY_IN_MID_AMOUNT,
	WE_SPIN_BUY_IN_HIGH_AMOUNT,
	WE_SPIN_BUY_IN_TEXT_KEYS
} from '../constants/we-spin-text-keys.js';
import WeSpinGameRoomBuyInButton from './we-spin-game-room-buy-in-button.js';
import WeSpinGameRoomBuyInModal from './we-spin-game-room-buy-in-modal.js';
import { LABEL_MAPPING, FREE_WESPIN } from '../constants/we-spin-boost-constants.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import { useCloseCurrentContext } from '~/hooks/use-close-context.js';
import {
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST
} from '../constants/we-spin-route-constants.js';
import { fetchCurrentEvent } from '../ducks/we-spin-home-game-duck.js';
import { selectEventByRef, selectIsStreamer } from '../ducks/we-spin-dashboard-duck.js';
import { selectLicense } from '../../../brand/ducks/selectors.js';
import { LICENSE_SGA } from '~/utils/license-utils.js';
import useLoginFlow from '~/hooks/use-login-flow.js';
import { Input, InputBorder, InputHint } from './we-spin-game-room-buy-in-base';

const BoxWithBackgroundColor = styled(Box)`
	background-color: var(--button-wrapper-bg);
`;

const BoxWithDisabledBackgroundColor = styled(Box)`
	background-color: ${({ disabled }) =>
		disabled ? 'var(--we-spin-game-room-buyin-disabled-bg, rgba(var(--base-12), 1))' : 'var(--button-wrapper-bg)'};

	> small {
		color: rgba(var(--primary), 1);
	}
`;

const BUY_IN_AMOUNT_TEXT_KEYS = [WE_SPIN_BUY_IN_LOW_AMOUNT, WE_SPIN_BUY_IN_MID_AMOUNT, WE_SPIN_BUY_IN_HIGH_AMOUNT];

const WeSpinGameRoomBuyIn = ({ streamView, buttonRef }) => {
	const location = useLocation();
	const router = useRouter();
	const isMounted = useIsMounted();
	const dispatch = useDispatch();
	const buyInEnabled = useSelector(selectWeSpinBuyInEnabled);

	const {
		currentGame,
		streamRef,
		streamerName,
		buyinAmounts,
		currencyCode,
		balance,
		balanceCap,
		balanceCapMet,
		language,
		noParticipants,
		boost,
		start,
		durationInMinutes,
		playerCapMet,
		isPrivateStreamer
	} = streamView || {};
	const [isReminderChecked, setIsReminderChecked] = useState(true);
	const [showFreeWeSpinQuestion, setShowFreeWeSpinQuestion] = useState(false);
	const isFreeWeSpin = boost && boost.type === FREE_WESPIN;
	const maxNoParticipantsCap = boost && boost.noPlayers;
	const showBuyInForm = !isFreeWeSpin;
	const { hasBetSizeRange, hasMultipleGamesSelection } = useSelector(selectConfiguration);
	const hasMadeDeposit = useSelector(selectHasMadeDeposit);
	const startDate = new Date(start);
	const endTime = new Date(startDate.getTime() + durationInMinutes * 60000);
	const formattedStartDate = date.format(startDate, 'ddd D MMM');
	const formattedStartTime = date.format(startDate, 'HH:mm');
	const formattedEndTime = date.format(endTime, 'HH:mm');

	const [submitting, setSubmitting] = useState(false);
	const [cappedAmount, setCappedAmount] = useState(balanceCap - balance);
	const authenticated = useSelector(authenticationSelector);

	const buyInAmount = useSelector(selectTemporaryBuyIn(streamRef || ''));
	// Disable amount checks for private stream
	const hasBuyInAmountChecks = useMemo(() => !!isPrivateStreamer, [isPrivateStreamer]);

	useEffect(() => {
		dispatch(fetchProfile());
	}, [dispatch, authenticated]);

	useEffect(() => {
		dispatch(fetchHasMadeDeposit());
	}, [dispatch]);

	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_BUY_IN_TEXT_KEYS);

	const [lowestBuyInAmount, setLowestBuyInAmount] = useState(0);

	const walletBalanceInPlayersCurrency = useSelector(selectBalance) || 0;
	const playerCurrency = useSelector(selectCurrency);

	const walletBalance = useSelector(selectPlayerBalanceInEventCurrency(streamRef)) || 0;

	useEffect(() => {
		if (streamRef && authenticated) {
			dispatch(playerBalanceInEventCurrency(streamRef));
		} else {
			setPlayerBalanceInEventCurrency(null, streamRef);
		}
		return () => {
			dispatch(setPlayerBalanceInEventCurrency(null, streamRef));
		};
	}, [dispatch, walletBalanceInPlayersCurrency, streamRef, authenticated]);

	const playersBoughtIntoSession = useSelector(selectPlayersBoughtIntoSession(streamRef)) || null;

	useEffect(() => {
		if (streamRef) {
			dispatch(fetchPlayersBoughtIntoSession(streamRef));
		}
	}, [dispatch, streamRef]);

	const setBuyInAmount = useCallback(
		amount => {
			dispatch(setTemporaryBuyIn(amount, streamRef));
		},
		[dispatch, streamRef]
	);

	useEffect(() => {
		if (authenticated && hasBuyInAmountChecks && walletBalance !== null && buyInAmount > walletBalance) {
			setBuyInAmount(Math.min(buyInAmount, walletBalance));
		}
		setLowestBuyInAmount(buyinAmounts[0]);
	}, [
		buyInAmount,
		walletBalance,
		authenticated,
		setLowestBuyInAmount,
		buyinAmounts,
		setBuyInAmount,
		hasBuyInAmountChecks
	]);

	const [buyInQuestion, setBuyInQuestion] = useState(false);
	const [adjustAmountQuestion, setAdjustAmountQuestion] = useState(false);
	const [usernameQuestion, setUsernameQuestion] = useState(false);

	const locale = useSelector(selectLocale);
	const displayName = useSelector(selectDisplayName);

	const { initializeLogin } = useLoginFlow();

	const updateBuyInAmount = useCallback(
		evt => {
			const value = evt.target.value.replace(/-/g, '');

			if (value || evt.target.value === '') {
				setBuyInAmount(value);
			}
		},

		[setBuyInAmount]
	);

	const showBuyInQuestion = useCallback(() => {
		if (hasBuyInAmountChecks && cappedAmount < buyInAmount) {
			setAdjustAmountQuestion(true);
		} else {
			setBuyInQuestion(true);
		}
	}, [setAdjustAmountQuestion, cappedAmount, buyInAmount, setBuyInQuestion, hasBuyInAmountChecks]);

	const onAdjustAmount = useCallback(() => {
		setAdjustAmountQuestion(false);
		setBuyInAmount(cappedAmount);
		setBuyInQuestion(true);
	}, [cappedAmount, setBuyInAmount]);

	const onNoAdjustClick = useCallback(() => {
		setAdjustAmountQuestion(false);
	}, []);

	const transformPath = useContextualPathTransformer();
	const showUsernameQuestionOrBuyInQuestion = useCallback(() => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		} else if (!displayName) {
			setUsernameQuestion(true);
		} else {
			showBuyInQuestion();
		}
	}, [authenticated, displayName, location, showBuyInQuestion, initializeLogin]);

	const overBalanceCapHandler = useCallback(
		error => {
			if (isMounted.current) {
				setBuyInQuestion(false);
				setCappedAmount(error.amount);
				setAdjustAmountQuestion(true);
			}
		},
		[isMounted]
	);

	const joinFreeWeSpin = useCallback(() => {
		if (authenticated) {
			if (playerCapMet) {
				dispatch(
					showNotification({
						messageKey: tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_PLAYER_CAP_EXCEEDED,
						color: 'red',
						autoHide: true,
						openInSidebar: true
					})
				);
			}
			setSubmitting(true);
			setShowFreeWeSpinQuestion(false);
			dispatch(spectatorRegisterFreeWespin(Number(0), streamRef, overBalanceCapHandler)).then(() => {
				if (!isMounted) {
					return;
				}

				const fromDashboard = location.state?.fromDashboard;
				setSubmitting(false);
				dispatch(fetchCurrentEvent());

				if (isPrivateStreamer) {
					router.replace(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS}/${streamRef}`));
				} else if (fromDashboard) {
					router.replace(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD));
				} else {
					router.replace(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/${streamRef}`));
				}
			});
		} else {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		}
	}, [
		authenticated,
		playerCapMet,
		streamRef,
		overBalanceCapHandler,
		location,
		dispatch,
		router,
		tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_PLAYER_CAP_EXCEEDED,
		transformPath,
		isMounted,
		isPrivateStreamer,
		initializeLogin
	]);

	const performBuyIn = useCallback(() => {
		if (authenticated) {
			if (hasBuyInAmountChecks && balanceCap - balance < buyInAmount) {
				dispatch(
					showNotification({
						messageKey: WE_SPIN_BUY_IN_AMOUNT_BALANCE_CAP_EXCEEDED,
						color: 'red',
						autoHide: true,
						openInSidebar: true
					})
				);
			} else {
				setSubmitting(true);
				setBuyInQuestion(false);
				dispatch(spectatorRegister(buyInAmount, streamRef, overBalanceCapHandler, isReminderChecked))
					.catch(() => {}) // Avoids an error log in Chrome
					.finally(() => {
						if (!isMounted) {
							return;
						}

						const fromDashboard = location.state?.fromDashboard;
						setSubmitting(false);
						dispatch(fetchCurrentEvent());

						if (isPrivateStreamer) {
							router.replace(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS}/${streamRef}`));
						} else if (fromDashboard) {
							router.replace(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD));
						} else {
							router.replace(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/${streamRef}`));
						}
					});
			}
		} else {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		}
	}, [
		authenticated,
		balance,
		balanceCap,
		buyInAmount,
		dispatch,
		router,
		location,
		overBalanceCapHandler,
		streamRef,
		isMounted,
		transformPath,
		isReminderChecked,
		hasBuyInAmountChecks,
		isPrivateStreamer,
		initializeLogin
	]);

	const onNoClick = useCallback(() => {
		setBuyInQuestion(false);
		setShowFreeWeSpinQuestion(false);
	}, []);

	const onCloseSidebar = useCloseCurrentContext();

	const proceedWithBuyIn = useCallback(() => {
		setUsernameQuestion(false);
		showBuyInQuestion();
	}, [setUsernameQuestion, showBuyInQuestion]);

	const buyInBalance = useMemo(
		() => (buyInAmount > 0 ? currencyFormatter.format(buyInAmount || 0, playerCurrency, locale) : ''),
		[buyInAmount, playerCurrency, locale]
	);

	const balancePot = useMemo(
		() => currencyFormatter.format(balance, playerCurrency, locale),
		[balance, playerCurrency, locale]
	);

	const newAmount = useMemo(
		() => (cappedAmount > 0 ? currencyFormatter.format(cappedAmount, playerCurrency, locale) : ''),
		[cappedAmount, playerCurrency, locale]
	);

	const walletBalanceFormatted = useMemo(
		() => (walletBalance !== null ? currencyFormatter.format(walletBalance, playerCurrency, locale) : ''),
		[walletBalance, playerCurrency, locale]
	);

	const showUsernameQuestionOrJoinFreeWeSpin = useCallback(() => {
		if (!authenticated) {
			initializeLogin({
				from: `${location.pathname}${location.search}`
			});
		}
		if (!displayName) {
			setUsernameQuestion(true);
		} else {
			setShowFreeWeSpinQuestion(true);
		}
	}, [authenticated, displayName, setUsernameQuestion, setShowFreeWeSpinQuestion, location, initializeLogin]);

	const proceedToJoinFreeWeSpin = () => {
		setUsernameQuestion(false);
		setShowFreeWeSpinQuestion(true);
	};

	const handleReminderCheckboxClick = () => {
		setIsReminderChecked(!isReminderChecked);
	};

	const isStreamer = useSelector(selectIsStreamer);
	const ownsThisEvent = !!useSelector(selectEventByRef(streamView.streamRef));
	const license = useSelector(selectLicense);

	const isBuyInDisallowed = license === LICENSE_SGA && isStreamer && !streamView.isPrivateStreamer && ownsThisEvent;

	if (isLoadingTranslations) return null;
	const inputPlaceholder = !buyInEnabled
		? tr.WE_SPIN_BUY_IN_AMOUNT_PLACEHOLDER_CUSTOM
		: tr.WE_SPIN_BUY_IN_AMOUNT_PLACEHOLDER;
	return (
		<>
			<WeSpinGameRoomBuyInModal
				adjustAmountQuestion={adjustAmountQuestion}
				buyInQuestion={buyInQuestion}
				usernameQuestion={usernameQuestion}
				balanceCapMet={balanceCapMet}
				submitting={submitting}
				tr={tr}
				proceedWithBuyIn={proceedWithBuyIn}
				proceedToJoinFreeWeSpin={proceedToJoinFreeWeSpin}
				onCloseSidebar={onCloseSidebar}
				onAdjustAmount={onAdjustAmount}
				onNoAdjustClick={onNoAdjustClick}
				buyInBalance={buyInBalance}
				newAmount={newAmount}
				streamerName={streamerName}
				currentGame={currentGame}
				onNoClick={onNoClick}
				performBuyIn={performBuyIn}
				showFreeWeSpinQuestion={showFreeWeSpinQuestion}
				joinFreeWeSpin={joinFreeWeSpin}
				formattedStartDate={formattedStartDate}
				formattedStartTime={formattedStartTime}
				formattedEndTime={formattedEndTime}
				isFreeWeSpin={isFreeWeSpin}
				isReminderChecked={isReminderChecked}
				handleReminderCheckboxClick={handleReminderCheckboxClick}
			/>
			<div className="bottom-half wespin-buyin-form">
				{showBuyInForm && (
					<>
						<div className="form">
							<fieldset className="u-relative u-oh u-p1">
								<Input
									className="input-amount u-text-c"
									disabled={isBuyInDisallowed}
									type="number"
									id="new-stream-game"
									min="0"
									placeholder={isBuyInDisallowed ? '' : inputPlaceholder}
									onChange={updateBuyInAmount}
									value={buyInAmount || ''}
									required
								/>
								<label htmlFor="new-stream-game">{inputPlaceholder}</label>
								<InputBorder
									className="form__border"
									isDenmarkFranchise={!buyInEnabled}
									buyInAmount={buyInAmount}
									lowestBuyInAmount={lowestBuyInAmount}
								/>
								{!buyInEnabled && buyInAmount < lowestBuyInAmount && (
									<InputHint className="hint" buyInAmount={buyInAmount}>
										{`${currencyFormatter.format(lowestBuyInAmount, playerCurrency, locale)} ${
											tr.WE_SPIN_BUY_IN_MINIMUM_AMOUNT
										}`}
									</InputHint>
								)}
							</fieldset>
						</div>
						{buyInEnabled && (
							<div className="u-flex u-pr1 u-pl1 buy-in-buttons">
								{buyinAmounts.map((amount, i) => (
									<div key={amount} className="u-text-c u-flex-grow-1">
										<Caption>{tr[BUY_IN_AMOUNT_TEXT_KEYS[i]]}</Caption>
										<WeSpinGameRoomBuyInButton
											amount={amount}
											enabled={
												!isBuyInDisallowed &&
												!submitting &&
												balanceCap >= balance + amount &&
												(!authenticated || (!!walletBalance && walletBalance >= amount))
											}
											formattedAmount={currencyFormatter.format(amount, playerCurrency, locale)}
											updateBuyInAmountCallback={updateBuyInAmount}
											globalAmount={buyInAmount}
										/>
									</div>
								))}
							</div>
						)}
					</>
				)}
				<div className="u-p1">
					{authenticated && playerCurrency !== currencyCode && (
						<Collapse
							label={`${tr.WE_SPIN_BUY_IN_MY_BALANCE_IN_LABEL} ${playerCurrency}`}
							text={tr.WE_SPIN_BUY_IN_MY_BALANCE_IN_TEXT}
							dataAt="collapse-button-games-excluded-bonus"
							value={walletBalanceFormatted}
							infoIcon
							className="we-spin-buyin__collapse"
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						/>
					)}
					{boost && (
						<Collapse
							label={LABEL_MAPPING[boost.type]}
							text={boost.description}
							dataAt="collapse-button-games-excluded-bonus"
							value={currencyFormatter.format(boost.amount, playerCurrency, locale)}
							infoIcon
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						/>
					)}
					<Collapse
						label={tr.WE_SPIN_BUY_IN_WESPIN_BALANCE_LABEL}
						text={tr.WE_SPIN_BUY_IN_WESPIN_BALANCE_TEXT}
						dataAt="collapse-button-games-excluded-bonus"
						value={balancePot}
						infoIcon
						className="we-spin-buyin__collapse"
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>

					<Collapse
						label={tr.WE_SPIN_BUY_IN_PLAYERS_LABEL}
						text={
							(playersBoughtIntoSession && playersBoughtIntoSession.join(', ')) ||
							tr.WE_SPIN_BUY_IN_PLAYERS_TEXT
						}
						dataAt="collapse-button-games-excluded-bonus"
						value={isFreeWeSpin ? `${noParticipants} / ${maxNoParticipantsCap}` : noParticipants}
						className="we-spin-buyin__collapse"
						infoIcon
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
					{language && (
						<Collapse
							label={tr.WE_SPIN_BUY_IN_LANGUAGE_LABEL}
							text={tr.WE_SPIN_BUY_IN_LANGUAGE_TEXT}
							className="we-spin-buyin__collapse"
							dataAt="collapse-button-games-excluded-bonus"
							value={tr[`WE_SPIN_BUY_IN_LANGUAGE_${language.toUpperCase()}`]}
							infoIcon
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						/>
					)}
					{hasBetSizeRange && (
						<Collapse
							label={tr.WE_SPIN_BET_SIZE_RANGE_LABEL}
							text={tr.WE_SPIN_BET_SIZE_RANGE_TEXT}
							className="we-spin-buyin__collapse"
							value={[abbrNum(streamView.betSizeRangeMin), abbrNum(streamView.betSizeRangeMax)]
								.map(v => (v ? `${v} ${streamView.currencyCode}` : '###'))
								.join(' - ')}
							infoIcon
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						/>
					)}
					{hasMultipleGamesSelection && (
						<Collapse
							label={`${tr.WE_SPIN_BUY_IN_WESPIN_GAMES_LABEL} (${streamView.games.length})`}
							text={false}
							dataAt="collapse-button-games-list"
							className="we-spin-buyin__collapse"
							iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
						>
							{streamView.games.map(({ gameName }) => (
								<Caption key={gameName}>{gameName}</Caption>
							))}
						</Collapse>
					)}

					<Collapse
						label={tr.WE_SPIN_BUY_IN_WESPIN_TOC_LABEL}
						text={tr.WE_SPIN_BUY_IN_WESPIN_TOC_TEXT}
						dataAt="collapse-button-games-excluded-bonus"
						className="we-spin-game-room__collapse"
						iconBottom={<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />}
					/>
				</div>
			</div>
			{!isFreeWeSpin &&
				buttonRef.current &&
				ReactDOM.createPortal(
					<BoxWithBackgroundColor flex="1" mb="0" p="5" className="wespin-large-buyin-button-container">
						<ButtonPrimary
							onClick={showUsernameQuestionOrBuyInQuestion}
							disabled={
								(authenticated && (walletBalance === null || walletBalance <= 0)) ||
								submitting ||
								balanceCapMet ||
								buyInAmount <= 0 ||
								Number(buyInAmount) < lowestBuyInAmount ||
								isBuyInDisallowed
							}
						>
							{`${tr.WE_SPIN_BUY_IN_BUTTON} ${buyInBalance}`}
						</ButtonPrimary>
					</BoxWithBackgroundColor>,
					buttonRef.current
				)}
			{isFreeWeSpin &&
				buttonRef.current &&
				ReactDOM.createPortal(
					<BoxWithDisabledBackgroundColor
						disabled={!hasMadeDeposit}
						flex="1"
						mb="0"
						p="5"
						display="flex"
						flexDirection="column"
						alignItems="center"
						className="wespin-buyin-button-large"
					>
						{!hasMadeDeposit && <Caption pb="5">{tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_DEPOSIT_REQUIRED}</Caption>}
						<ButtonPrimary
							onClick={showUsernameQuestionOrJoinFreeWeSpin}
							disabled={submitting || !hasMadeDeposit || playerCapMet}
							whileTap={!hasMadeDeposit && undefined}
						>
							{tr.WE_SPIN_BUY_IN_FREE_WE_SPIN_REGISTER}
						</ButtonPrimary>
					</BoxWithDisabledBackgroundColor>,
					buttonRef.current
				)}
		</>
	);
};

export default WeSpinGameRoomBuyIn;
